<template>
    <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
    </div>
    <div v-else id="jitsi-container">
        <JitsiMeeting :domain="domain" :roomName="roomName" :jwt="token" :configOverwrite="configOverwrite"
            :interfaceConfigOverwrite="interfaceConfigOverwrite" :onApiReady="handleApiReady"
            :onReadyToClose="handleReadyToClose" :onError="handleError" :lang="lang"
            @get-iframe-ref-on-api-ready="handleIframeReady" />
    </div>
</template>

<script>
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import { useRouter } from 'vue-router';
import { translate } from '@/utils/translate';
import { ref, onMounted, nextTick } from 'vue';
import { JitsiMeeting } from '@jitsi/vue-sdk';
import { sendUserAction } from '@/utils/socket'; // Import the function


export default {
    name: 'JitsiComponent',
    components: {
        JitsiMeeting
    },
    props: {
        event: {
            type: Object,
            required: true,
        },
        attendee: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const loading = ref(true);
        const domain = ref('');
        const roomName = ref('');
        const token = ref('');
        const lang = ref(window.vueAppConfig?.settings?.language || 'cs'); // Extract language setting
        console.log(window.vueAppConfig);
        const configOverwrite = ref({
            disableInviteFunctions: true,
            hiddenPremeetingButtons: ['invite'],
            whiteboard: {
                enabled: false
            },
            defaultLogoUrl: window.vueAppConfig?.settings?.logo_url || '',
        });
        const interfaceConfigOverwrite = ref({
            HIDE_INVITE_MORE_HEADER: true,
        });
        const router = useRouter();

        const initializeJitsiMeeting = async () => {
            const signatureResponse = await generateMeetingSignature();
            if (signatureResponse.status) {
                domain.value = signatureResponse.data.domain;
                roomName.value = signatureResponse.data.room_name;
                token.value = signatureResponse.data.token;
                loading.value = false;

                nextTick(() => {
                    // Any post-initialization tasks can be handled here if necessary
                });
            } else {
                notify({
                    type: "info",
                    group: "info",
                    title: translate('Info'),
                    text: translate(signatureResponse.message)
                }, 10000);

                if (signatureResponse.reload === true) {
                    loading.value = true;
                    /*  setTimeout(() => {
                          window.location.reload();
                      }, 5000);*/
                } else {
                    console.error(translate('Error generating meeting signature'));
                }
            }
        };

        const generateMeetingSignature = async () => {
            try {
                const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/sign/meeting`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        event_id: props.event.id,
                        session_id: cookie(),
                        brand_id: window.vueAppConfig.info.id,
                    }),
                });
                return response.json();
            } catch (error) {
                console.error(translate('Error fetching signature'), error);
                return { status: false, message: translate('Error fetching signature') };
            }
        };

        const handleApiReady = (api) => {
            console.log('Jitsi API Ready', api);
            // No need to call adjustIframeSize here
        };

        const handleIframeReady = (parentNode) => {
            const iframe = parentNode.querySelector('iframe');
            if (iframe) {
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
            } else {
                console.error('IFrame element not found within parentNode');
            }
        };

        const handleReadyToClose = () => {
            sendUserAction('left', 'jitsi');
            console.log('Jitsi Meeting is ready to be closed');
            const leaveUrl = router.resolve({
                name: 'EventPage',
                params: { type: props.event.type.path, id: props.event.slug }
            }).href;
            router.push(leaveUrl);
        };

        const handleError = (error) => {
            console.error('Error in Jitsi Meeting', error);
        };

        onMounted(() => {
            sendUserAction('open', 'jitsi');
            initializeJitsiMeeting();
        });

        return {
            loading,
            domain,
            roomName,
            token,
            configOverwrite,
            interfaceConfigOverwrite,
            lang, // Include the language in the return statement
            handleApiReady,
            handleReadyToClose,
            handleError,
            handleIframeReady
        };
    }
};
</script>


<style scoped>
#jitsi-container {
    width: 100%;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    background: #000;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.spinner {
    border: 16px solid #f3f3f3;
    border-top: 16px solid #3498db;
    border-radius: 50%;
    width: 120px;
    height: 120px;
    animation: spin 2s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
